import { WorkOrderSource } from '@atlas/types'
import { Badge, cn, Focusable, Tooltip, TooltipContent } from '@atlas/ui-primitives'
import { CloudDownload, Hourglass } from 'lucide-react'

/** Whether the order came from Workfront, so the sync owns some of its fields. */
export const isFromWorkfront = (order: { source: string }): boolean => order.source === WorkOrderSource.WORKFRONT

const OWNED_HINT = 'Workfront owns this value: the next sync overwrites a change made here.'

/**
 * Marks a value the Workfront sync owns. `compact` is the icon alone, for a row of a dense
 * grid where a word on every line would drown the values; it keeps the same accessible name
 * and explains itself on hover and focus.
 */
export function FromWorkfrontBadge({ compact = false, className }: { compact?: boolean; className?: string }) {
	if (compact) {
		return (
			<Tooltip>
				<Focusable>
					<span
						role="img"
						aria-label="From Workfront"
						tabIndex={0}
						className={cn('inline-flex text-ds-text-subtle', className)}
					>
						<CloudDownload className="h-3.5 w-3.5" aria-hidden />
					</span>
				</Focusable>
				<TooltipContent>{OWNED_HINT}</TooltipContent>
			</Tooltip>
		)
	}
	return (
		<Badge size="sm" variant="muted" className={cn('gap-1', className)} title={OWNED_HINT}>
			<CloudDownload className="h-3 w-3" aria-hidden />
			From Workfront
		</Badge>
	)
}

/**
 * The order is held by a mapping error: the sync leaves it as it is until the value maps.
 * The titles are the adapter's own wording for the errors, so an admin finds them as named.
 */
export function WaitingForMappingBadge({ titles }: { titles: string[] }) {
	const reason = titles.length ? titles.join(' · ') : null
	return (
		<span className="inline-flex flex-wrap items-center gap-2">
			<Badge size="sm" variant="warning" className="gap-1">
				<Hourglass className="h-3 w-3" aria-hidden />
				Waiting for a mapping
			</Badge>
			{reason ? <span className="text-ds-sm text-ds-text-muted">{reason}</span> : null}
		</span>
	)
}
